import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';
import { confirm } from '@/components/molecules/ConfirmDialog';
import EmptyState from '@/components/molecules/EmptyState';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import { useSetUserClientAccess } from '@/domain/users';

import styles from './UserAccessModal.module.scss';

interface Location {
    id: number;
    name: string;
}

export interface UserAccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number;
    userName: string;
    clientId: number;
    clientLocations: Location[];
    /** IDs the user currently has access to; empty means full client access (ADMIN role) */
    currentLocationIds: number[];
    /** True when the user currently has full client-admin access */
    isFullClientAccess: boolean;
    onSaved: () => void;
}

export function UserAccessModal({
    isOpen,
    onClose,
    userId,
    userName,
    clientId,
    clientLocations,
    currentLocationIds,
    isFullClientAccess,
    onSaved,
}: UserAccessModalProps) {
    const { t } = useTranslation();
    const { mutateAsync: setAccess, isPending: isLoading } = useSetUserClientAccess();
    // useId's colons are valid in an id but break jsdom's selector engine in the tests.
    const fullAccessId = useId().replace(/:/g, '');

    const [fullAccess, setFullAccess] = useState(isFullClientAccess);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(currentLocationIds));
    const [sortAccessIds, setSortAccessIds] = useState<Set<number>>(
        () => new Set(currentLocationIds)
    );
    const [saveError, setSaveError] = useState<string | null>(null);
    const prevIsOpenRef = useRef(false);

    // Sync state only when the modal opens — not on parent re-renders while it stays open
    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;

        if (!justOpened) {
            return;
        }

        setFullAccess(isFullClientAccess);
        setSelectedIds(new Set(currentLocationIds));
        setSaveError(null);
        setSortAccessIds(
            isFullClientAccess
                ? new Set(clientLocations.map((location) => location.id))
                : new Set(currentLocationIds)
        );
    }, [isOpen, isFullClientAccess, currentLocationIds, clientLocations]);

    const toggleLocation = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleFullAccessChange = (checked: boolean) => {
        setFullAccess(checked);
        if (!checked && selectedIds.size === 0) {
            setSelectedIds(new Set(clientLocations.map((location) => location.id)));
        }
    };

    const handleSave = async () => {
        setSaveError(null);

        const save = () =>
            setAccess({
                userId,
                clientId,
                locationIds: fullAccess ? [] : [...selectedIds],
                fullClientAccess: fullAccess,
            });

        // Revoking every location asks first; the dialog then saves and shows a failure.
        if (!fullAccess && selectedIds.size === 0) {
            const revoked = await confirm({
                title: t('userAccess.revokeTitle'),
                message: t('userAccess.confirmRevoke'),
                confirmLabel: t('userAccess.revokeTitle'),
                errorFallback: t('userAccess.saveError'),
                onConfirm: save,
            });
            if (revoked) {
                onSaved();
                onClose();
            }
            return;
        }

        try {
            await save();
            onSaved();
            onClose();
        } catch {
            setSaveError(t('userAccess.saveError'));
        }
    };

    const selectedCount = selectedIds.size;
    const totalCount = clientLocations.length;

    const sortedClientLocations = useMemo(() => {
        return [...clientLocations].sort((a, b) => {
            const aHasAccess = sortAccessIds.has(a.id);
            const bHasAccess = sortAccessIds.has(b.id);

            if (aHasAccess !== bHasAccess) {
                return aHasAccess ? -1 : 1;
            }

            return a.name.localeCompare(b.name);
        });
    }, [clientLocations, sortAccessIds]);

    const summaryText = fullAccess
        ? t('userAccess.allLocations')
        : selectedCount === 0
          ? t('userAccess.noLocations')
          : t('userAccess.nOfM', { n: selectedCount, m: totalCount });

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            centered={false}
            title={t('userAccess.modalTitle', { user: userName })}>
            <ModalBody className={styles.modalBody}>
                <Card
                    variant="subtle"
                    radius="sm"
                    padding="none"
                    className={styles.fullAccessToggle}>
                    <Checkbox
                        id={fullAccessId}
                        checked={fullAccess}
                        onCheckedChange={(checked) => handleFullAccessChange(checked)}
                        label={t('userAccess.fullClientAccess')}
                        rowClassName={styles.checkboxRow}
                    />
                </Card>

                {clientLocations.length === 0 ? (
                    <EmptyState title={t('userAccess.noLocationsAvailable')} appearance="quiet" />
                ) : (
                    <div className={styles.locationsList}>
                        {sortedClientLocations.map((loc) => {
                            const checked = fullAccess || selectedIds.has(loc.id);
                            const disabled = fullAccess;
                            return (
                                <LocationCheckbox
                                    key={loc.id}
                                    location={loc}
                                    checked={checked}
                                    disabled={disabled}
                                    onToggle={toggleLocation}
                                />
                            );
                        })}
                    </div>
                )}

                <p className={styles.summary}>{summaryText}</p>
                {saveError && (
                    <Alert tone="danger" size="small">
                        {saveError}
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="secondary"
                    trackingId="user_access_cancel"
                    onClick={onClose}
                    disabled={isLoading}>
                    {t('common.actions.cancel')}
                </Button>
                <Button
                    variant="primary"
                    trackingId="user_access_save"
                    onClick={handleSave}
                    disabled={isLoading}>
                    {t('common.actions.save')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}

interface LocationCheckboxProps {
    location: Location;
    checked: boolean;
    disabled: boolean;
    onToggle: (id: number) => void;
}

function LocationCheckbox({ location, checked, disabled, onToggle }: LocationCheckboxProps) {
    const id = useId().replace(/:/g, '');
    return (
        <Card
            variant="subtle"
            radius="sm"
            padding="none"
            className={classNames(styles.locationItem, disabled && styles.disabled)}>
            <Checkbox
                id={id}
                checked={checked}
                disabled={disabled}
                onCheckedChange={() => onToggle(location.id)}
                label={location.name}
                rowClassName={classNames(styles.checkboxRow, styles.locationRow)}
                labelClassName={styles.locationLabel}
            />
        </Card>
    );
}

export default UserAccessModal;
